import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Waves, Plane, Landmark, Anchor, LoaderCircle, MapPin } from "lucide-react";

const TYPE_META = {
    beach: { icon: Waves, label: "Plaj", color: "cyan" },
    airport: { icon: Plane, label: "Havalimanı", color: "indigo" },
    attraction: { icon: Landmark, label: "Gezi Yeri", color: "amber" },
    marina: { icon: Anchor, label: "Marina", color: "violet" },
};

/**
 * Nearby POIs panel — shows beaches/airports/attractions around a hotel
 * with calculated distance in km.
 *
 * Props:
 *   hotelId    - hotel UUID
 *   radius_km  - search radius, default 50 (cover province-wide)
 */
export default function NearbyPOIs({ hotelId, radius_km = 50 }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState(null);

    useEffect(() => {
        if (!hotelId) return;
        setLoading(true);
        api.get(`/hotels/${hotelId}/nearby-pois?radius_km=${radius_km}&limit=15`)
            .then((r) => {
                setItems(r.data.items || []);
                setNote(r.data.note || null);
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [hotelId, radius_km]);

    if (loading) {
        return (
            <div className="p-4 text-center" data-testid="nearby-loading">
                <LoaderCircle className="h-5 w-5 animate-spin text-sky-500 mx-auto" />
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="p-4 text-sm text-slate-500 text-center" data-testid="nearby-empty">
                {note || "Bu otelin çevresinde tanımlı bir konum bulunamadı."}
            </div>
        );
    }

    // Group by type
    const groups = items.reduce((acc, p) => {
        const t = p.type || "other";
        acc[t] = acc[t] || [];
        acc[t].push(p);
        return acc;
    }, {});

    return (
        <div className="space-y-4" data-testid="nearby-pois">
            {Object.entries(groups).map(([type, list]) => {
                const meta = TYPE_META[type] || { icon: MapPin, label: type, color: "slate" };
                const Icon = meta.icon;
                return (
                    <div key={type}>
                        <h3 className={`text-xs font-bold uppercase tracking-widest text-${meta.color}-600 dark:text-${meta.color}-400 flex items-center gap-1.5 mb-2`}>
                            <Icon className="h-3.5 w-3.5" /> {meta.label}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {list.map((p) => (
                                <div
                                    key={p.slug}
                                    data-testid={`poi-${p.slug}`}
                                    className={`flex items-center justify-between gap-2 p-3 rounded-xl bg-${meta.color}-50 dark:bg-${meta.color}-950/20 border border-${meta.color}-100 dark:border-${meta.color}-900/40`}
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{p.name}</div>
                                        {p.description && (
                                            <div className="text-[11px] text-slate-500 truncate">{p.description}</div>
                                        )}
                                    </div>
                                    <div className={`text-xs font-bold flex-shrink-0 px-2 py-1 rounded-lg bg-${meta.color}-100 dark:bg-${meta.color}-900/50 text-${meta.color}-700 dark:text-${meta.color}-300`}>
                                        {p.distance_km < 1
                                            ? `${Math.round(p.distance_km * 1000)} m`
                                            : `${p.distance_km.toFixed(1)} km`}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
